import React, { useCallback, useState } from 'react';

import AddToAgendaComponent from 'components/MeshList/AddToAgendaComponent';
import { PlayIcon } from 'components/PlayIcon';
import CompetencyProgressBar from 'screens/oneOnOne/levelUp/competencySidePanel/components/CompetencyProgressBar';
import useLevelUpSidePanelContext from 'screens/oneOnOne/levelUp/competencySidePanel/context';
import styled, { css } from 'styled-components';
import { StyledBox, StyledGrid, StyledTooltip } from 'styles/common';

import { MESH_LIST_TABS } from 'constants/ProjectConstants';
import { ChevronRightIcon, StyledIconV2 } from 'constants/icons';
import theme from 'constants/theme';

import { Typography13 } from 'uiToolkit/Typography';

import { useLevelUpContext } from '../context';
import CompetencyPeriodSelect, { StyledCompetencyPeriodSelect } from './CompetencyPeriodSelect';
import SubCompetencyList from './SubCompetencyList';

const StyledPlayIcon = styled(PlayIcon)`
  ${(props) =>
    !props.$hasSubCompetencies &&
    css`
      opacity: 0.4;
      pointer-events: none;
    `}
  margin-top: 16px;
  cursor: pointer;
  flex: 0 0 auto;
`;

const StyledAddToAgendaComponent = styled(AddToAgendaComponent)`
  margin-left: auto;
`;

const RightArrowIcon = ({ className, onClick }) => (
  <StyledTooltip className={className} title="Open Details Panel">
    <StyledIconV2
      size="fixed10px"
      containerSize="fixed24px"
      circularOutline
      color="primary"
      bgColor="white"
      style={{ flex: '0 0 auto' }}
      rightGutter={1}
      onClick={onClick}
    >
      <ChevronRightIcon size="fixed10px" />
    </StyledIconV2>
  </StyledTooltip>
);

const StyledRightArrowIcon = styled(RightArrowIcon)`
  visibility: hidden;
  cursor: pointer;
`;

const RowContainer = styled.div`
  display: flex;
  align-items: stretch;
  padding: 0rem 1.5rem;
  cursor: pointer;
  &:hover {
    ${StyledAddToAgendaComponent}, ${StyledRightArrowIcon} {
      visibility: visible;
    }
    background-color: ${(props) => props.theme.palette.background.blue.light.v2};

    ${StyledCompetencyPeriodSelect} {
      background-color: white;
      .MuiOutlinedInput-notchedOutline {
        border-color: ${(props) => props.theme.palette.primary.main};
      }
    }
  }
`;

const VerticalLine = styled.div`
  margin-left: 12px;
  height: 100%;
  border-left: 1px solid ${(props) => props.theme.palette.primary.main};
`;

const ExpandIconContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-right: 16px;
`;

const CompetencyRow = ({
  levelUpId, // only need for add to agenda.
  competencyId,
  name,
  targetPeriodStart,
  subCompetencies,
  handleTargetDateChange,
  handleProgressStatusChange,
  showAddToAgenda = false, // read from redux store to check if to enable add to agenda,
  status,
  competencyStatus,
  progressBar,
  progress,
}) => {
  const {
    state: { reporteeId, checkInInstanceId, checkInParentId, filterEmpIds, isLoggedInEmployeeManager },
  } = useLevelUpContext();

  const {
    state: { levelUpTargetDates, levelUpProgressStatuses },
    openLevelUpSidePanel,
  } = useLevelUpSidePanelContext();

  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpansion = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, [setIsExpanded]);

  const handleOpenLevelUpSidePanel = () => {
    openLevelUpSidePanel({
      reporteeId,
      competencyId,
      levelUpId,
      levelUpCompetencyId: null,
      isLoggedInEmployeeManager,
      checkInInstanceId,
      checkInParentId,
      filterEmpIds,
      isOneOnOne: true,
    });
  };

  const levelUpCompetency = {
    levelUpId,
    competencyId,
    subCompetencyId: null,
  };

  const hasSubCompetencies = Array.isArray(subCompetencies) && subCompetencies.length > 0;

  return (
    <>
      <RowContainer onClick={handleOpenLevelUpSidePanel}>
        <ExpandIconContainer>
          <StyledPlayIcon
            fillColor={hasSubCompetencies ? theme.palette.primary.main : theme.palette.grey.main}
            backgroundColor={hasSubCompetencies ? theme.palette.primary.main : theme.palette.grey.main}
            onClick={toggleExpansion}
            $hasSubCompetencies={hasSubCompetencies}
          />
          {isExpanded ? <VerticalLine /> : null}
        </ExpandIconContainer>

        <StyledBox width="100%" topGutter={2} bottomGutter={2}>
          <StyledGrid container alignItems="center" justify="space-between" bottomGutter={2}>
            <Typography13 weight={600} color={theme.palette.grey.dark}>
              {name}
            </Typography13>
            {showAddToAgenda ? (
              <StyledAddToAgendaComponent
                levelUpCompetency={levelUpCompetency}
                type={MESH_LIST_TABS.levelUp.taskType}
              />
            ) : null}
            <StyledRightArrowIcon onClick={handleOpenLevelUpSidePanel} />
          </StyledGrid>

          <StyledGrid container justify="space-between" alignItems="center">
            <StyledGrid item xs={7} style={{ position: 'relative', left: '-12px' }}>
              <CompetencyProgressBar
                filledBlocks={progressBar?.filledBlocks}
                totalBlocks={progressBar?.totalBlocks}
                progress={progress}
                barHeight="0.3125rem"
                percentage={progressBar?.percentage}
              />
            </StyledGrid>
            <StyledGrid container justify="flex-end" item xs={5}>
              <CompetencyPeriodSelect
                competencyId={competencyId}
                selectedPeriod={targetPeriodStart}
                handleTargetDateChange={handleTargetDateChange}
                levelUpTargetDates={levelUpTargetDates}
              />
            </StyledGrid>
          </StyledGrid>
        </StyledBox>
      </RowContainer>
      {isExpanded ? <SubCompetencyList subCompetencies={subCompetencies} /> : null}
    </>
  );
};

export default CompetencyRow;
